import type { MouseEvent } from "react";
import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";

import type { CurrentLevelEnrollment } from "./Path";

interface EnrollmentPromptModalProps {
  visible: boolean;
  onHide: () => void;
  onEnroll: (overrideOtherPaths?: boolean) => void;
  pathName?: string;
  isLoading?: boolean;
  currentLevelEnrollment?: CurrentLevelEnrollment;
  title?: string;
  reassigned?: boolean;
}

const EnrollmentPromptModal = ({
  visible,
  onHide,
  onEnroll,
  pathName,
  isLoading,
  currentLevelEnrollment,
  title,
  reassigned = false,
}: EnrollmentPromptModalProps) => {
  const handleEnrollClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (currentLevelEnrollment) {
      confirmPopup({
        target: event.currentTarget,
        message: `Enrolling in ${title} will pause your enrollment in ${currentLevelEnrollment.name} in the ${currentLevelEnrollment.levelName} Level. Would you like to continue?`,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes",
        rejectLabel: "No",
        accept: () => onEnroll(true),
        style: { maxWidth: "400px" },
        pt: {
          root: {
            role: "alertdialog",
            "aria-modal": "true",
            "aria-label": "Confirm enrollment change",
          },
          content: {
            role: "region",
            "aria-label": "Confirm enrollment change",
          },
        },
      });
    } else {
      onEnroll();
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      {!reassigned && (
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          onClick={onHide}
          disabled={isLoading}
        />
      )}
      <Button label="Enroll" onClick={handleEnrollClick} loading={isLoading} />
    </div>
  );

  return (
    <Dialog
      header={reassigned ? "Enrollment Required" : "Enroll in Path?"}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "450px" }}
      modal
      closable={!reassigned && !isLoading}
      draggable={false}
      aria-describedby="enrollment-modal-description"
    >
      <p className="body-regular" id="enrollment-modal-description">
        {reassigned ? (
          <>
            The <strong>{pathName}</strong> path has been reassigned. You must enroll to access the
            lessons and receive credit for your progress.
          </>
        ) : (
          <>
            You have started a lesson that is part of the <strong>{pathName}</strong> path. To
            complete this path and receive a certificate, please enroll.
          </>
        )}
      </p>
      <ConfirmPopup
        pt={{
          root: {
            role: "alertdialog",
            "aria-modal": "true",
            "aria-label": "Confirm enrollment change",
          },
          content: {
            role: "region",
            "aria-label": "Confirm enrollment change",
          },
        }}
      />
    </Dialog>
  );
};

export default EnrollmentPromptModal;
